import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  Paperclip, UploadSimple, Trash, FileText, FilePdf, FileImage, File, Download, SpinnerGap
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const fileIcon = (contentType) => {
  if (!contentType) return File;
  if (contentType.includes('pdf')) return FilePdf;
  if (contentType.includes('image')) return FileImage;
  if (contentType.includes('text') || contentType.includes('csv')) return FileText;
  return File;
};

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const FileAttachments = ({ entityType, entityId, compact = false }) => {
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (!entityId) return;
    try {
      const data = await api.getAttachments(entityType, entityId);
      setAttachments(data);
    } catch {
      // silently fail on load
    }
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      try {
        await api.uploadAttachment(entityType, entityId, file);
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    toast.success(`${files.length} file(s) attached`);
    setUploading(false);
    await load();
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete ${name}?`)) return;
    try {
      await api.deleteAttachment(id);
      toast.success('Attachment removed');
      await load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <input ref={fileRef} type="file" multiple className="hidden" onChange={handleUpload} />
        <button
          onClick={() => fileRef.current?.click()}
          className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-lg transition-colors"
          style={{ border: '1px solid rgba(148,163,184,0.15)', color: '#94A3B8' }}
          disabled={uploading}
          data-testid="attach-file-btn"
        >
          {uploading ? <SpinnerGap size={12} className="animate-spin" /> : <Paperclip size={12} />}
          {uploading ? 'Uploading...' : `Attach (${attachments.length})`}
        </button>
        {attachments.map(att => {
          const Icon = fileIcon(att.content_type);
          return (
            <a
              key={att.id}
              href={`${API}/api/attachments/file/${att.stored_name}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-md transition-colors"
              style={{ background: 'rgba(99,102,241,0.08)', color: '#A5B4FC', border: '1px solid rgba(99,102,241,0.15)' }}
              title={att.filename}
            >
              <Icon size={11} /> {att.filename.length > 20 ? att.filename.slice(0, 18) + '...' : att.filename}
            </a>
          );
        })}
      </div>
    );
  }

  return (
    <div data-testid="file-attachments">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-medium flex items-center gap-1.5" style={{ color: '#CBD5E1' }}>
          <Paperclip size={14} /> Attachments ({attachments.length})
        </h4>
        <input ref={fileRef} type="file" multiple className="hidden" onChange={handleUpload} />
        <Button
          variant="outline"
          size="sm"
          className="btn-secondary text-xs"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid="upload-attachment-btn"
        >
          {uploading ? <SpinnerGap size={12} className="animate-spin mr-1" /> : <UploadSimple size={12} className="mr-1" />}
          {uploading ? 'Uploading...' : 'Upload File'}
        </Button>
      </div>

      {attachments.length === 0 ? (
        <div
          className="rounded-lg p-4 text-center cursor-pointer transition-colors"
          style={{ border: '1px dashed rgba(148,163,184,0.15)', color: '#64748B' }}
          onClick={() => fileRef.current?.click()}
        >
          <UploadSimple size={20} className="mx-auto mb-1" />
          <p className="text-xs">Drop files here or click to upload</p>
          <p className="text-[10px] mt-0.5">PDFs, images, spreadsheets, documents</p>
        </div>
      ) : (
        <div className="space-y-1">
          {attachments.map(att => {
            const Icon = fileIcon(att.content_type);
            return (
              <div key={att.id} className="flex items-center gap-2.5 p-2 rounded-lg transition-colors group" style={{ border: '1px solid rgba(148,163,184,0.08)' }}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(99,102,241,0.08)' }}>
                  <Icon size={16} style={{ color: '#818CF8' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate" style={{ color: '#E2E8F0' }}>{att.filename}</p>
                  <p className="text-[10px]" style={{ color: '#64748B' }}>
                    {formatSize(att.size)} &middot; {new Date(att.uploaded_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <a
                    href={`${API}/api/attachments/file/${att.stored_name}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 rounded hover:bg-slate-700 transition-colors"
                    style={{ color: '#94A3B8' }}
                    title="Download"
                  >
                    <Download size={13} />
                  </a>
                  <button
                    onClick={() => handleDelete(att.id, att.filename)}
                    className="p-1.5 rounded hover:bg-red-900/20 transition-colors"
                    style={{ color: '#EF4444' }}
                    title="Delete"
                  >
                    <Trash size={13} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FileAttachments;
